import { NumberInputField } from '../../Form';
import { forwardRef, useCallback, useMemo } from 'react';
import { IRowMeta } from '../../../redux/slices/oldBuilder';
import { errorPrimitiveSelector } from '../../../redux/selectors/builder';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { removeErrorAction } from '../../../redux/slices/oldBuilder';
import { INumberInputFieldProps } from '../../Form/NumberInputField';

interface IProps extends INumberInputFieldProps {
  /**
   * This pair allows errorSelector to locate the correct value which
   * represents the error for the particular field
   */
  rowMeta: IRowMeta;
  errorKey: string;
}

/**
 * Wrapper over NumberInputField that enhances it with additional error message
 * from the Redux store. The extra error message is set when the user attempts
 * to execute the query but certain fields aren't populated
 *
 * hasError and errorMessage from the props take precedence over the Redux
 * error message
 *
 * If you don't need integration with global Redux error handling, use
 * NumberInputField directly
 *
 */
export default forwardRef<HTMLInputElement, IProps>(
  ({ onChange, errorMessage, hasError, rowMeta, errorKey, ...rest }, ref) => {
    const dispatch = useAppDispatch();
    const errorSelector = useMemo(errorPrimitiveSelector, []);
    const storeHasError = useAppSelector((state) =>
      Boolean(errorSelector(state, rowMeta, errorKey)),
    );
    const storeErrorMessage = storeHasError ? 'Value is required' : undefined;

    const onChangeAndClearStoreError: INumberInputFieldProps['onChange'] =
      useCallback(
        (value, isError) => {
          dispatch(removeErrorAction({ rowMeta, errorKey }));
          onChange?.(value, isError);
        },
        [dispatch, errorKey, onChange, rowMeta],
      );

    return (
      <NumberInputField
        ref={ref}
        onChange={onChangeAndClearStoreError}
        errorMessage={errorMessage ?? storeErrorMessage}
        hasError={hasError || storeHasError}
        {...rest}
      />
    );
  },
);
